/**
 * NZ Grid Map Component
 *
 * Mapbox-based visualization of New Zealand's electricity grid.
 * Shows substations, transmission lines, and real-time data.
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import MapGL, {
  Source,
  Layer,
  Popup,
  NavigationControl,
  ScaleControl,
  GeolocateControl,
} from 'react-map-gl';
import type { MapLayerMouseEvent } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useStore } from '../store';
import {
  apiFetch,
  dashboardApi,
  nzGridApi,
  RegionalStress,
  Provenance,
  SitesGeoJSON,
  StructuresGeoJSON,
  SpansGeoJSON,
  CorridorsGeoJSON,
  CookStraitZoneGeoJSON,
  TransmissionLinesGeoJSON,
} from '../services/api';
import DataBadge from './DataBadge';
import DataProvenanceRow from './DataProvenanceRow';
import CurrentStatePanel from './CurrentStatePanel';

// Your Mapbox token
const MAPBOX_TOKEN = 'pk.eyJ1IjoiYnRnNzc3IiwiYSI6ImNtajBiM3JsMjAwc3ozaXFuMXYyOHVjd3UifQ.CrF3AUm1HL_4CII7AguH5w';

// NZ Grid Data - Substations
const FALLBACK_SUBSTATIONS = [
  // North Island - Upper
  { name: 'Otahuhu', code: 'OTA', lat: -36.935, lon: 174.855, voltage: 220, island: 'NI', load_mw: 280 },
  { name: 'Penrose', code: 'PEN', lat: -36.907, lon: 174.823, voltage: 220, island: 'NI', load_mw: 280 },
  { name: 'Henderson', code: 'HEN', lat: -36.878, lon: 174.632, voltage: 220, island: 'NI', load_mw: 280 },
  { name: 'Albany', code: 'ALB', lat: -36.728, lon: 174.706, voltage: 220, island: 'NI', load_mw: 280 },
  { name: 'Pakuranga', code: 'PAK', lat: -36.912, lon: 174.907, voltage: 220, island: 'NI', load_mw: 280 },

  // North Island - Waikato
  { name: 'Hamilton', code: 'HAM', lat: -37.794, lon: 175.283, voltage: 220, island: 'NI', load_mw: 105 },
  { name: 'Huntly', code: 'HLY', lat: -37.559, lon: 175.155, voltage: 220, island: 'NI', load_mw: 35, gen_mw: 1088 },
  { name: 'Te Kowhai', code: 'TKH', lat: -37.727, lon: 175.208, voltage: 220, island: 'NI', load_mw: 35 },

  // North Island - Bay of Plenty
  { name: 'Kawerau', code: 'KAW', lat: -38.08, lon: 176.713, voltage: 220, island: 'NI', load_mw: 35, gen_mw: 175 },
  { name: 'Edgecumbe', code: 'EDG', lat: -37.977, lon: 176.833, voltage: 220, island: 'NI', load_mw: 35 },
  { name: 'Tauranga', code: 'TRG', lat: -37.686, lon: 176.172, voltage: 220, island: 'NI', load_mw: 105 },

  // North Island - Central/Geothermal
  { name: 'Wairakei', code: 'WRK', lat: -38.622, lon: 176.115, voltage: 220, island: 'NI', load_mw: 35, gen_mw: 847 },
  { name: 'Ohaaki', code: 'OHK', lat: -38.523, lon: 176.314, voltage: 220, island: 'NI', load_mw: 35, gen_mw: 196 },
  { name: 'Whakamaru', code: 'WKM', lat: -38.421, lon: 175.835, voltage: 220, island: 'NI', load_mw: 35, gen_mw: 582 },

  // North Island - Lower
  { name: 'Bunnythorpe', code: 'BPE', lat: -40.283, lon: 175.635, voltage: 220, island: 'NI', load_mw: 35, gen_mw: 1069 },
  { name: 'Haywards', code: 'HAY', lat: -41.158, lon: 174.976, voltage: 220, island: 'NI', load_mw: 210, gen_mw: 203 },
  { name: 'Wellington', code: 'WIL', lat: -41.287, lon: 174.779, voltage: 220, island: 'NI', load_mw: 210 },

  // South Island - Upper
  { name: 'Benmore', code: 'BEN', lat: -44.534, lon: 170.234, voltage: 220, island: 'SI', load_mw: 35, gen_mw: 540 },
  { name: 'Waitaki', code: 'WTK', lat: -44.876, lon: 170.476, voltage: 220, island: 'SI', load_mw: 35, gen_mw: 105 },
  { name: 'Twizel', code: 'TWZ', lat: -44.268, lon: 170.095, voltage: 220, island: 'SI', load_mw: 35, gen_mw: 688 },

  // South Island - Canterbury
  { name: 'Islington', code: 'ISL', lat: -43.541, lon: 172.507, voltage: 220, island: 'SI', load_mw: 140 },
  { name: 'Bromley', code: 'BRY', lat: -43.532, lon: 172.706, voltage: 220, island: 'SI', load_mw: 140 },
  { name: 'Kikiwa', code: 'KIK', lat: -41.517, lon: 172.881, voltage: 220, island: 'SI', load_mw: 35 },

  // South Island - Lower
  { name: 'Manapouri', code: 'MAN', lat: -45.507, lon: 167.478, voltage: 220, island: 'SI', load_mw: 35, gen_mw: 850 },
  { name: 'Invercargill', code: 'INV', lat: -46.408, lon: 168.348, voltage: 220, island: 'SI', load_mw: 35, gen_mw: 58 },
  { name: 'Roxburgh', code: 'ROX', lat: -45.541, lon: 169.318, voltage: 220, island: 'SI', load_mw: 35, gen_mw: 320 },
  { name: 'Clyde', code: 'CYD', lat: -45.194, lon: 169.32, voltage: 220, island: 'SI', load_mw: 35, gen_mw: 464 },
  { name: 'Halfway Bush', code: 'HWB', lat: -45.87, lon: 170.457, voltage: 220, island: 'SI', load_mw: 35, gen_mw: 36 },
];

// Transmission line connections
const FALLBACK_LINES = [
  // Auckland backbone
  { from: 'ALB', to: 'HEN', rating: 500 },
  { from: 'HEN', to: 'OTA', rating: 500 },
  { from: 'OTA', to: 'PEN', rating: 500 },
  { from: 'OTA', to: 'PAK', rating: 400 },

  // Auckland to Waikato
  { from: 'OTA', to: 'HLY', rating: 600 },
  { from: 'HLY', to: 'HAM', rating: 500 },
  { from: 'HLY', to: 'TKH', rating: 400 },

  // Waikato to BOP/Central
  { from: 'HAM', to: 'TRG', rating: 400 },
  { from: 'TRG', to: 'EDG', rating: 300 },
  { from: 'EDG', to: 'KAW', rating: 300 },

  // Central NI (geothermal region)
  { from: 'HLY', to: 'WKM', rating: 600 },
  { from: 'WKM', to: 'WRK', rating: 500 },
  { from: 'WRK', to: 'OHK', rating: 400 },
  { from: 'OHK', to: 'KAW', rating: 300 },

  // Central to Lower NI
  { from: 'WKM', to: 'BPE', rating: 600 },
  { from: 'BPE', to: 'HAY', rating: 600 },
  { from: 'HAY', to: 'WIL', rating: 400 },

  // HVDC (Cook Strait)
  { from: 'HAY', to: 'BEN', rating: 1200, isHVDC: true },

  // South Island backbone
  { from: 'BEN', to: 'TWZ', rating: 600 },
  { from: 'TWZ', to: 'WTK', rating: 500 },
  { from: 'BEN', to: 'ISL', rating: 600 },
  { from: 'ISL', to: 'BRY', rating: 400 },
  { from: 'ISL', to: 'KIK', rating: 300 },

  // Lower SI
  { from: 'BEN', to: 'ROX', rating: 400 },
  { from: 'ROX', to: 'CYD', rating: 500 },
  { from: 'ROX', to: 'HWB', rating: 300 },
  { from: 'HWB', to: 'INV', rating: 300 },

  // Manapouri connection
  { from: 'MAN', to: 'INV', rating: 850 },
  { from: 'MAN', to: 'TWZ', rating: 600 },
];

interface SubstationBase {
  name: string;
  code: string;
  lat: number;
  lon: number;
  voltage: number;
  island: string;
  load_mw?: number;
  gen_mw?: number;
  bus_id?: number;
}

interface LineDefinition {
  from: string;
  to: string;
  rating: number;
  isHVDC?: boolean;
}

interface GeneratorRecord {
  substation: string;
  capacity_mw: number;
}

interface HvdcTerminals {
  north_terminal: string;
  south_terminal: string;
}

interface WitsPricePoint {
  node_code: string;
  node_name?: string;
  price_nzd_mwh: number;
  timestamp: string;
  trading_period?: number;
  node_type?: string;
}

interface GeoJsonFeature {
  type: 'Feature';
  id?: number | string;
  geometry: {
    type: 'Point' | 'LineString';
    coordinates: number[] | number[][];
  };
  properties?: Record<string, unknown>;
}

interface FeatureCollection {
  type: 'FeatureCollection';
  features: GeoJsonFeature[];
}

const FALLBACK_HVDC: HvdcTerminals = {
  north_terminal: 'HAY',
  south_terminal: 'BEN',
};

const isHvdcLink = (from: string, to: string, hvdc: HvdcTerminals) => {
  const fromCode = from.toUpperCase();
  const toCode = to.toUpperCase();
  const north = hvdc.north_terminal.toUpperCase();
  const south = hvdc.south_terminal.toUpperCase();
  return (fromCode === north && toCode === south) || (fromCode === south && toCode === north);
};

const buildLinesGeoJSON = (
  lines: LineDefinition[],
  lookup: Record<string, SubstationBase>
): FeatureCollection => {
  const features: GeoJsonFeature[] = [];
  lines.forEach((line, idx) => {
    const from = lookup[line.from];
    const to = lookup[line.to];
    if (!from || !to) return;
    features.push({
      type: 'Feature',
      id: idx,
      geometry: {
        type: 'LineString',
        coordinates: [
          [from.lon, from.lat],
          [to.lon, to.lat],
        ],
      },
      properties: {
        from: line.from,
        to: line.to,
        rating: line.rating,
        isHVDC: line.isHVDC || false,
      },
    });
  });
  return {
    type: 'FeatureCollection',
    features,
  };
};

const FALLBACK_SUBSTATION_LOOKUP = Object.fromEntries(
  FALLBACK_SUBSTATIONS.map((s) => [s.code, s])
) as Record<string, SubstationBase>;

const FALLBACK_LINES_GEOJSON = buildLinesGeoJSON(
  FALLBACK_LINES as LineDefinition[],
  FALLBACK_SUBSTATION_LOOKUP
);

const normalizeHvdcTerminals = (hvdc: unknown): HvdcTerminals => {
  if (
    hvdc &&
    typeof (hvdc as HvdcTerminals).north_terminal === 'string' &&
    typeof (hvdc as HvdcTerminals).south_terminal === 'string'
  ) {
    return {
      north_terminal: (hvdc as HvdcTerminals).north_terminal,
      south_terminal: (hvdc as HvdcTerminals).south_terminal,
    };
  }
  return FALLBACK_HVDC;
};

const buildGridFromGeojson = (
  geojson: FeatureCollection,
  generators: GeneratorRecord[],
  hvdc: HvdcTerminals
): { substations: SubstationBase[]; lines: FeatureCollection } => {
  const generatorTotals: Record<string, number> = {};
  for (const gen of generators) {
    if (!gen?.substation) continue;
    const capacity = Number(gen.capacity_mw);
    if (!Number.isFinite(capacity)) continue;
    generatorTotals[gen.substation] = (generatorTotals[gen.substation] || 0) + capacity;
  }

  const substations: SubstationBase[] = [];
  const busIdToCode = new Map<number, string>();

  for (const feature of geojson.features || []) {
    if (feature.geometry?.type !== 'Point') continue;
    const coords = feature.geometry.coordinates as number[];
    if (!Array.isArray(coords) || coords.length < 2) continue;

    const props = (feature.properties || {}) as Record<string, unknown>;
    const codeValue = typeof props.code === 'string' ? props.code : '';
    const idValue = typeof props.id === 'number' ? props.id : Number(props.id);
    const code = codeValue || (Number.isFinite(idValue) ? String(idValue) : 'BUS');
    const name = typeof props.name === 'string' ? props.name : code;
    const voltage =
      typeof props.voltage_kv === 'number'
        ? props.voltage_kv
        : typeof props.voltage === 'number'
        ? props.voltage
        : 220;
    const island = typeof props.island === 'string' ? props.island : 'NI';
    const load = typeof props.load_mw === 'number' ? props.load_mw : 0;
    const genCapacity = generatorTotals[code] || 0;

    substations.push({
      name,
      code,
      lat: coords[1],
      lon: coords[0],
      voltage,
      island,
      load_mw: load,
      gen_mw: genCapacity,
      bus_id: Number.isFinite(idValue) ? idValue : undefined,
    });

    if (Number.isFinite(idValue)) {
      busIdToCode.set(idValue, code);
    }
  }

  const lineFeatures: GeoJsonFeature[] = [];
  for (const feature of geojson.features || []) {
    if (feature.geometry?.type !== 'LineString') continue;
    const props = (feature.properties || {}) as Record<string, unknown>;
    const rating =
      typeof props.rating_mva === 'number'
        ? props.rating_mva
        : typeof props.rating === 'number'
        ? props.rating
        : 0;
    const fromBusId = typeof props.from_bus === 'number' ? props.from_bus : Number(props.from_bus);
    const toBusId = typeof props.to_bus === 'number' ? props.to_bus : Number(props.to_bus);
    const fromCode = Number.isFinite(fromBusId) ? busIdToCode.get(fromBusId) : undefined;
    const toCode = Number.isFinite(toBusId) ? busIdToCode.get(toBusId) : undefined;
    const isHVDC = fromCode && toCode ? isHvdcLink(fromCode, toCode, hvdc) : false;

    lineFeatures.push({
      type: 'Feature',
      id: feature.id ?? `${fromCode || fromBusId}-${toCode || toBusId}`,
      geometry: feature.geometry,
      properties: {
        ...props,
        from: fromCode || props.from_bus,
        to: toCode || props.to_bus,
        rating,
        isHVDC,
      },
    });
  }

  return {
    substations,
    lines: {
      type: 'FeatureCollection',
      features: lineFeatures,
    },
  };
};

interface PopupInfo {
  longitude: number;
  latitude: number;
  feature: any;
}

interface NZGridMapProps {
  className?: string;
}

const NZGridMap: React.FC<NZGridMapProps> = ({ className = '' }) => {
  const { solverResult, solverConfig, gridSummary } = useStore();

  const [viewState, setViewState] = useState({
    longitude: 172.5,
    latitude: -41.5,
    zoom: 5,
    pitch: 0,
    bearing: 0,
  });

  const [substations, setSubstations] = useState<SubstationBase[]>(FALLBACK_SUBSTATIONS);
  const [linesGeoJSON, setLinesGeoJSON] = useState<FeatureCollection>(FALLBACK_LINES_GEOJSON);
  const [popupInfo, setPopupInfo] = useState<PopupInfo | null>(null);
  const [showGenerators, setShowGenerators] = useState(true);
  const [showLoads, setShowLoads] = useState(true);
  const [showVoltageOverlay, setShowVoltageOverlay] = useState(true);
  const [showStressOverlay, setShowStressOverlay] = useState(false);
  const [showPrices, setShowPrices] = useState(true);
  const [showLineLoading, setShowLineLoading] = useState(true);
  const [isUsingFallbackTopology, setIsUsingFallbackTopology] = useState(true);
  const [stressData, setStressData] = useState<RegionalStress[]>([]);
  const [gridMetrics, setGridMetrics] = useState<{
    total_capacity_mw: number;
    total_load_mw: number;
  } | null>(null);
  const [witsPrices, setWitsPrices] = useState<WitsPricePoint[]>([]);
  const [witsLoading, setWitsLoading] = useState(false);
  const [witsError, setWitsError] = useState<string | null>(null);
  const [witsProvenance, setWitsProvenance] = useState<Provenance | null>(null);
  const [metricsProvenance, setMetricsProvenance] = useState<Provenance | null>(null);

  // Transpower ArcGIS layers (LIVE data)
  const [showTranspowerSites, setShowTranspowerSites] = useState(false);
  const [showTranspowerStructures, setShowTranspowerStructures] = useState(false);
  const [showTranspowerSpans, setShowTranspowerSpans] = useState(false);
  const [showTranspowerCorridors, setShowTranspowerCorridors] = useState(false);
  const [showCookStraitZone, setShowCookStraitZone] = useState(false);
  const [showTranspowerLines, setShowTranspowerLines] = useState(false);

  const [transpowerSites, setTranspowerSites] = useState<SitesGeoJSON | null>(null);
  const [transpowerStructures, setTranspowerStructures] = useState<StructuresGeoJSON | null>(null);
  const [transpowerSpans, setTranspowerSpans] = useState<SpansGeoJSON | null>(null);
  const [transpowerCorridors, setTranspowerCorridors] = useState<CorridorsGeoJSON | null>(null);
  const [cookStraitZone, setCookStraitZone] = useState<CookStraitZoneGeoJSON | null>(null);
  const [transpowerLines, setTranspowerLines] = useState<TransmissionLinesGeoJSON | null>(null);
  const [transpowerLoading, setTranspowerLoading] = useState<Record<string, boolean>>({});
  const [transpowerErrors, setTranspowerErrors] = useState<Record<string, string | null>>({});

  // Current State Panel (LIVE vs MODELLED view)
  const [currentStatePanelCollapsed, setCurrentStatePanelCollapsed] = useState(false);

  const fetchGridData = useCallback(async () => {
    try {
      const [geojsonResult, generatorsResult, hvdcResult] = await Promise.allSettled([
        nzGridApi.getGeoJSON(undefined, solverConfig.load_factor),
        nzGridApi.getGenerators(),
        nzGridApi.getHVDC(),
      ]);

      if (geojsonResult.status !== 'fulfilled') {
        throw geojsonResult.reason;
      }

      const geojson = geojsonResult.value as FeatureCollection;
      const generators = generatorsResult.status === 'fulfilled'
        ? (generatorsResult.value as GeneratorRecord[])
        : [];
      const hvdc = normalizeHvdcTerminals(
        hvdcResult.status === 'fulfilled' ? hvdcResult.value : null
      );

      const gridData = buildGridFromGeojson(geojson, generators, hvdc);
      if (gridData.substations.length === 0) {
        throw new Error('No substations found in grid GeoJSON');
      }

      setSubstations(gridData.substations);
      setLinesGeoJSON(gridData.lines);
      setIsUsingFallbackTopology(false);
    } catch (e) {
      console.warn('Failed to load grid topology, using fallback data:', e);
      setSubstations(FALLBACK_SUBSTATIONS);
      setLinesGeoJSON(FALLBACK_LINES_GEOJSON);
      setIsUsingFallbackTopology(true);
    }
  }, [solverConfig.load_factor]);

  const fetchStressData = useCallback(async () => {
    try {
      const metrics = await dashboardApi.getMetrics(solverConfig.load_factor);
      setStressData(metrics.regional_stress);
      setGridMetrics({
        total_capacity_mw: metrics.grid.total_capacity_mw,
        total_load_mw: metrics.grid.total_load_mw,
      });
      setMetricsProvenance(metrics.provenance || null);
    } catch (e) {
      console.warn('Failed to fetch regional stress data:', e);
      setMetricsProvenance(null);
    }
  }, [solverConfig.load_factor]);

  const fetchWitsPrices = useCallback(async () => {
    if (!showPrices) return;
    setWitsLoading(true);
    setWitsError(null);
    try {
      const response = await apiFetch('/wits/prices');
      if (!response.ok) {
        const err = await response.json().catch(() => null);
        throw new Error(err?.detail || 'Failed to fetch WITS prices');
      }
      const data = await response.json();
      setWitsProvenance((data?.provenance as Provenance | undefined) || null);
      const items = Array.isArray(data) ? data : Array.isArray(data?.prices) ? data.prices : [];
      const normalized = items
        .map((item: any) => {
          const node_code = String(item?.node_code || item?.node || '').trim();
          const price = item?.price_nzd_mwh ?? item?.price ?? item?.value;
          const price_nzd_mwh = typeof price === 'number' ? price : Number(price);
          if (!node_code || !Number.isFinite(price_nzd_mwh)) return null;
          return {
            node_code,
            node_name: item?.node_name || item?.node_name || item?.nodeName,
            price_nzd_mwh,
            timestamp: item?.timestamp || data?.timestamp || new Date().toISOString(),
            trading_period: item?.trading_period ?? item?.tradingPeriod,
            node_type: item?.node_type || item?.nodeType,
          } satisfies WitsPricePoint;
        })
        .filter(Boolean) as WitsPricePoint[];
      setWitsPrices(normalized);
    } catch (e) {
      setWitsError(e instanceof Error ? e.message : 'Failed to fetch WITS prices');
      setWitsPrices([]);
      setWitsProvenance(null);
    } finally {
      setWitsLoading(false);
    }
  }, [showPrices]);

  // Transpower ArcGIS data fetchers
  const fetchTranspowerSites = useCallback(async () => {
    if (!showTranspowerSites) return;
    setTranspowerLoading(prev => ({ ...prev, sites: true }));
    setTranspowerErrors(prev => ({ ...prev, sites: null }));
    try {
      const data = await nzGridApi.getSitesGeoJSON();
      setTranspowerSites(data);
    } catch (e) {
      setTranspowerErrors(prev => ({ ...prev, sites: e instanceof Error ? e.message : 'Failed to load sites' }));
    } finally {
      setTranspowerLoading(prev => ({ ...prev, sites: false }));
    }
  }, [showTranspowerSites]);

  const fetchTranspowerStructures = useCallback(async () => {
    if (!showTranspowerStructures) return;
    setTranspowerLoading(prev => ({ ...prev, structures: true }));
    setTranspowerErrors(prev => ({ ...prev, structures: null }));
    try {
      // Request all ~38,000 structures for complete data
      const data = await nzGridApi.getStructuresGeoJSON(undefined, undefined, 40000);
      setTranspowerStructures(data);
    } catch (e) {
      setTranspowerErrors(prev => ({ ...prev, structures: e instanceof Error ? e.message : 'Failed to load structures' }));
    } finally {
      setTranspowerLoading(prev => ({ ...prev, structures: false }));
    }
  }, [showTranspowerStructures]);

  const fetchTranspowerSpans = useCallback(async () => {
    if (!showTranspowerSpans) return;
    setTranspowerLoading(prev => ({ ...prev, spans: true }));
    setTranspowerErrors(prev => ({ ...prev, spans: null }));
    try {
      // Request all ~38,000 spans for complete data
      const data = await nzGridApi.getSpansGeoJSON(undefined, 40000);
      setTranspowerSpans(data);
    } catch (e) {
      setTranspowerErrors(prev => ({ ...prev, spans: e instanceof Error ? e.message : 'Failed to load spans' }));
    } finally {
      setTranspowerLoading(prev => ({ ...prev, spans: false }));
    }
  }, [showTranspowerSpans]);

  const fetchTranspowerCorridors = useCallback(async () => {
    if (!showTranspowerCorridors) return;
    setTranspowerLoading(prev => ({ ...prev, corridors: true }));
    setTranspowerErrors(prev => ({ ...prev, corridors: null }));
    try {
      const data = await nzGridApi.getCorridorsGeoJSON();
      setTranspowerCorridors(data);
    } catch (e) {
      setTranspowerErrors(prev => ({ ...prev, corridors: e instanceof Error ? e.message : 'Failed to load corridors' }));
    } finally {
      setTranspowerLoading(prev => ({ ...prev, corridors: false }));
    }
  }, [showTranspowerCorridors]);

  const fetchCookStraitZone = useCallback(async () => {
    if (!showCookStraitZone) return;
    setTranspowerLoading(prev => ({ ...prev, cookStrait: true }));
    setTranspowerErrors(prev => ({ ...prev, cookStrait: null }));
    try {
      const data = await nzGridApi.getCookStraitZoneGeoJSON();
      setCookStraitZone(data);
    } catch (e) {
      setTranspowerErrors(prev => ({ ...prev, cookStrait: e instanceof Error ? e.message : 'Failed to load Cook Strait zone' }));
    } finally {
      setTranspowerLoading(prev => ({ ...prev, cookStrait: false }));
    }
  }, [showCookStraitZone]);

  const fetchTranspowerLines = useCallback(async () => {
    if (!showTranspowerLines) return;
    setTranspowerLoading(prev => ({ ...prev, lines: true }));
    setTranspowerErrors(prev => ({ ...prev, lines: null }));
    try {
      const data = await nzGridApi.getTransmissionLinesGeoJSON();
      setTranspowerLines(data);
    } catch (e) {
      setTranspowerErrors(prev => ({ ...prev, lines: e instanceof Error ? e.message : 'Failed to load transmission lines' }));
    } finally {
      setTranspowerLoading(prev => ({ ...prev, lines: false }));
    }
  }, [showTranspowerLines]);

  useEffect(() => {
    fetchGridData();
  }, [fetchGridData]);

  useEffect(() => {
    fetchStressData();
    const interval = setInterval(fetchStressData, 10000);
    return () => clearInterval(interval);
  }, [fetchStressData]);

  useEffect(() => {
    if (!showPrices) return;
    fetchWitsPrices();
    const interval = setInterval(fetchWitsPrices, 60_000);
    return () => clearInterval(interval);
  }, [showPrices, fetchWitsPrices]);

  // Transpower ArcGIS data fetch effects
  useEffect(() => {
    if (showTranspowerSites) fetchTranspowerSites();
  }, [showTranspowerSites, fetchTranspowerSites]);

  useEffect(() => {
    if (showTranspowerStructures) fetchTranspowerStructures();
  }, [showTranspowerStructures, fetchTranspowerStructures]);

  useEffect(() => {
    if (showTranspowerSpans) fetchTranspowerSpans();
  }, [showTranspowerSpans, fetchTranspowerSpans]);

  useEffect(() => {
    if (showTranspowerCorridors) fetchTranspowerCorridors();
  }, [showTranspowerCorridors, fetchTranspowerCorridors]);

  useEffect(() => {
    if (showCookStraitZone) fetchCookStraitZone();
  }, [showCookStraitZone, fetchCookStraitZone]);

  useEffect(() => {
    if (showTranspowerLines) fetchTranspowerLines();
  }, [showTranspowerLines, fetchTranspowerLines]);

  // Build voltage lookup from solver results
  const voltageLookup = useMemo(() => {
    if (!solverResult) return {};
    const lookup: Record<string, { vm_pu: number; va_deg: number; voltage_status: string; p_mw: number; q_mvar: number }> = {};
    for (const bus of solverResult.bus_results) {
      lookup[bus.code] = {
        vm_pu: bus.vm_pu,
        va_deg: bus.va_deg,
        voltage_status: bus.voltage_status,
        p_mw: bus.p_mw,
        q_mvar: bus.q_mvar,
      };
    }
    return lookup;
  }, [solverResult]);

  const stressLookup = useMemo(() => {
    const lookup: Record<string, RegionalStress> = {};
    for (const region of stressData) {
      lookup[region.region_code] = region;
    }
    return lookup;
  }, [stressData]);

  const stressColorExpression: any[] = [
    'interpolate',
    ['linear'],
    ['coalesce', ['get', 'stress_level'], 0],
    0.0, '#22c55e',
    0.4, '#84cc16',
    0.7, '#eab308',
    0.85, '#f97316',
    1.0, '#ef4444',
  ];

  const priceLookup = useMemo(() => {
    const lookup: Record<string, WitsPricePoint> = {};
    for (const price of witsPrices) {
      const code = price.node_code.slice(0, 3).toUpperCase();
      const existing = lookup[code];
      if (!existing) {
        lookup[code] = price;
        continue;
      }
      const existingTs = new Date(existing.timestamp).getTime();
      const nextTs = new Date(price.timestamp).getTime();
      if (Number.isFinite(nextTs) && nextTs >= existingTs) {
        lookup[code] = price;
      }
    }
    return lookup;
  }, [witsPrices]);

  const linesWithSolverMetrics = useMemo(() => {
    if (!solverResult) return linesGeoJSON;
    const lookup = new Map<number, { loading_percent: number; p_mw: number }>();
    for (const br of solverResult.branch_results || []) {
      if (!Number.isFinite(br.branch_id)) continue;
      lookup.set(br.branch_id, {
        loading_percent: br.loading_percent,
        p_mw: br.p_mw,
      });
    }

    return {
      ...linesGeoJSON,
      features: (linesGeoJSON.features || []).map((feature: any) => {
        const props = (feature?.properties || {}) as Record<string, unknown>;
        const id = typeof props.id === 'number' ? props.id : Number(props.id);
        const metrics = Number.isFinite(id) ? lookup.get(id) : undefined;
        if (!metrics) return feature;
        return {
          ...feature,
          properties: {
            ...props,
            loading_percent: metrics.loading_percent,
            p_mw: metrics.p_mw,
            overloaded: metrics.loading_percent >= 100,
          },
        };
      }),
    };
  }, [solverResult, linesGeoJSON]);

  const baseSubstationsGeoJSON = useMemo(() => ({
    type: 'FeatureCollection' as const,
    features: substations.map((sub, idx) => ({
      type: 'Feature' as const,
      id: idx,
      geometry: {
        type: 'Point' as const,
        coordinates: [sub.lon, sub.lat],
      },
      properties: {
        ...(() => {
          const price = priceLookup[sub.code];
          const priceLabel = price ? `$${price.price_nzd_mwh.toFixed(0)}` : null;
          return {
            price_nzd_mwh: price?.price_nzd_mwh ?? null,
            price_timestamp: price?.timestamp ?? null,
            price_label: priceLabel,
            hasPrice: !!price,
          };
        })(),
        name: sub.name,
        code: sub.code,
        voltage: sub.voltage,
        island: sub.island,
        load_mw: sub.load_mw || 0,
        gen_mw: sub.gen_mw || 0,
        hasGenerator: !!sub.gen_mw,
      },
    })),
  }), [substations, priceLookup]);

  // Build substations GeoJSON with voltage data
  const substationsWithVoltage = useMemo(() => {
    if (!solverResult || !showVoltageOverlay) return baseSubstationsGeoJSON;

    return {
      type: 'FeatureCollection' as const,
      features: substations.map((sub, idx) => {
        const voltageData = voltageLookup[sub.code];
        const stressInfo = stressLookup[sub.code];
        const price = priceLookup[sub.code];
        const priceLabel = price ? `$${price.price_nzd_mwh.toFixed(0)}` : null;
        return {
          type: 'Feature' as const,
          id: idx,
          geometry: {
            type: 'Point' as const,
            coordinates: [sub.lon, sub.lat],
          },
          properties: {
            name: sub.name,
            code: sub.code,
            voltage: sub.voltage,
            island: sub.island,
            load_mw: voltageData?.p_mw ?? stressInfo?.load_mw ?? sub.load_mw ?? 0,
            gen_mw: sub.gen_mw || 0,
            hasGenerator: !!sub.gen_mw,
            vm_pu: voltageData?.vm_pu ?? 1.0,
            va_deg: voltageData?.va_deg ?? 0,
            voltage_status: voltageData?.voltage_status ?? 'normal',
            q_mvar: voltageData?.q_mvar ?? 0,
            hasVoltageData: !!voltageData,
            stress_level: stressInfo?.stress_level ?? 0,
            stress_load_mw: stressInfo?.load_mw ?? 0,
            stress_capacity_mw: stressInfo?.capacity_mw ?? 0,
            hasStress: !!stressInfo,
            price_nzd_mwh: price?.price_nzd_mwh ?? null,
            price_timestamp: price?.timestamp ?? null,
            price_label: priceLabel,
            hasPrice: !!price,
          },
        };
      }),
    };
  }, [solverResult, showVoltageOverlay, voltageLookup, stressLookup, substations, baseSubstationsGeoJSON, priceLookup]);

  const substationsWithMetrics = useMemo(() => {
    if (solverResult && showVoltageOverlay) {
      return substationsWithVoltage;
    }

    return {
      type: 'FeatureCollection' as const,
      features: substations.map((sub, idx) => {
        const stressInfo = stressLookup[sub.code];
        const price = priceLookup[sub.code];
        const priceLabel = price ? `$${price.price_nzd_mwh.toFixed(0)}` : null;
        return {
          type: 'Feature' as const,
          id: idx,
          geometry: {
            type: 'Point' as const,
            coordinates: [sub.lon, sub.lat],
          },
          properties: {
            name: sub.name,
            code: sub.code,
            voltage: sub.voltage,
            island: sub.island,
            load_mw: stressInfo?.load_mw ?? sub.load_mw ?? 0,
            gen_mw: sub.gen_mw || 0,
            hasGenerator: !!sub.gen_mw,
            vm_pu: 1.0,
            va_deg: 0,
            voltage_status: 'normal',
            q_mvar: 0,
            hasVoltageData: false,
            stress_level: stressInfo?.stress_level ?? 0,
            stress_load_mw: stressInfo?.load_mw ?? 0,
            stress_capacity_mw: stressInfo?.capacity_mw ?? 0,
            hasStress: !!stressInfo,
            price_nzd_mwh: price?.price_nzd_mwh ?? null,
            price_timestamp: price?.timestamp ?? null,
            price_label: priceLabel,
            hasPrice: !!price,
          },
        };
      }),
    };
  }, [solverResult, showVoltageOverlay, substationsWithVoltage, stressLookup, substations, priceLookup]);

  const hasStressData = stressData.length > 0;
  const showVoltageColors = solverResult && showVoltageOverlay && !showStressOverlay;
  const showVoltageLegend = showVoltageColors;
  const loadCircleColor: any = showStressOverlay
    ? stressColorExpression
    : showVoltageColors
    ? [
        'match',
        ['get', 'voltage_status'],
        'normal', '#22c55e',
        'warning', '#f59e0b',
        'critical', '#ef4444',
        '#60a5fa',
      ]
    : '#60a5fa';
  const generatorCircleColor: any = showStressOverlay
    ? stressColorExpression
    : showVoltageColors
    ? [
        'match',
        ['get', 'voltage_status'],
        'normal', '#22c55e',
        'warning', '#f59e0b',
        'critical', '#ef4444',
        '#22c55e',
      ]
    : '#22c55e';

  const onClick = useCallback((event: MapLayerMouseEvent) => {
    const feature = event.features?.[0];
    if (feature) {
      const coords = feature.geometry.type === 'Point'
        ? feature.geometry.coordinates
        : null;
      if (coords) {
        setPopupInfo({
          longitude: coords[0],
          latitude: coords[1],
          feature: feature.properties,
        });
      }
    }
  }, []);

  const onMouseEnter = useCallback(() => {
    // Change cursor to pointer on hover
  }, []);

  const onMouseLeave = useCallback(() => {
    // Reset cursor
  }, []);

  // Summary stats
  const stats = useMemo(() => {
    const solverSummary = solverResult?.summary;
    const fallbackGeneration = substations.reduce((sum, s) => sum + (s.gen_mw || 0), 0);
    const fallbackLoad = substations.reduce((sum, s) => sum + (s.load_mw || 0), 0);
    const totalGen = solverSummary?.total_generation_mw
      ?? gridMetrics?.total_capacity_mw
      ?? gridSummary?.total_capacity_mw
      ?? fallbackGeneration;
    const totalLoad = solverSummary?.total_load_mw
      ?? gridMetrics?.total_load_mw
      ?? fallbackLoad;
    const substationsCount = gridSummary?.total_substations ?? substations.length;
    const linesCount = gridSummary?.branches?.length ?? linesGeoJSON.features.length;
    const generationLabel = solverSummary ? 'Total Generation' : 'Total Capacity';
    return { totalGen, totalLoad, substations: substationsCount, lines: linesCount, generationLabel };
  }, [solverResult, gridMetrics, gridSummary, substations, linesGeoJSON]);

  return (
    <div className={`relative w-full h-full ${className}`}>
      <MapGL
        {...viewState}
        onMove={(evt) => setViewState(evt.viewState)}
        mapboxAccessToken={MAPBOX_TOKEN}
        mapStyle="mapbox://styles/mapbox/dark-v11"
        interactiveLayerIds={['substations', 'substations-gen']}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        style={{ width: '100%', height: '100%' }}
      >
        {/* Navigation controls */}
        <NavigationControl position="top-right" />
        <ScaleControl position="bottom-right" />
        <GeolocateControl position="top-right" />

        {/* Transmission lines */}
        <Source id="lines" type="geojson" data={linesWithSolverMetrics}>
          <Layer
            id="lines-normal"
            type="line"
            filter={['!', ['get', 'isHVDC']]}
            paint={{
              'line-color': solverResult && showLineLoading
                ? [
                    'interpolate',
                    ['linear'],
                    ['coalesce', ['get', 'loading_percent'], 0],
                    0, '#4ade80',
                    80, '#eab308',
                    100, '#ef4444',
                    130, '#ef4444',
                  ]
                : '#4ade80',
              'line-width': solverResult && showLineLoading
                ? [
                    'interpolate',
                    ['linear'],
                    ['coalesce', ['get', 'loading_percent'], 0],
                    0, 2,
                    80, 3,
                    100, 4,
                    130, 6,
                  ]
                : [
                    'interpolate',
                    ['linear'],
                    ['get', 'rating'],
                    300, 2,
                    600, 3,
                    1200, 4,
                  ],
              'line-opacity': 0.8,
            }}
          />
          <Layer
            id="lines-hvdc"
            type="line"
            filter={['get', 'isHVDC']}
            paint={{
              'line-color': '#f59e0b',
              'line-width': 4,
              'line-opacity': 0.9,
              'line-dasharray': [2, 1],
            }}
          />
        </Source>

        {/* Substations - loads (colored by voltage when results available) */}
        {showLoads && (
          <Source id="substations" type="geojson" data={substationsWithMetrics}>
            <Layer
              id="substations"
              type="circle"
              filter={['!', ['get', 'hasGenerator']]}
              paint={{
                'circle-radius': [
                  'interpolate',
                  ['linear'],
                  ['get', 'load_mw'],
                  35, 6,
                  150, 10,
                  300, 14,
                ],
                'circle-color': loadCircleColor,
                'circle-stroke-color': '#fff',
                'circle-stroke-width': 2,
                'circle-opacity': 0.9,
              }}
            />
          </Source>
        )}

        {/* Substations - generators (colored by voltage when results available) */}
        {showGenerators && (
          <Source id="substations-gen" type="geojson" data={substationsWithMetrics}>
            <Layer
              id="substations-gen"
              type="circle"
              filter={['get', 'hasGenerator']}
              paint={{
                'circle-radius': [
                  'interpolate',
                  ['linear'],
                  ['get', 'gen_mw'],
                  100, 8,
                  500, 12,
                  1000, 16,
                ],
                'circle-color': generatorCircleColor,
                'circle-stroke-color': '#fff',
                'circle-stroke-width': 2,
                'circle-opacity': 0.9,
              }}
            />
          </Source>
        )}

        {/* Substation labels */}
        <Source id="labels" type="geojson" data={baseSubstationsGeoJSON}>
          <Layer
            id="substation-labels"
            type="symbol"
            layout={{
              'text-field': ['get', 'code'],
              'text-size': 10,
              'text-offset': [0, 1.5],
              'text-anchor': 'top',
            }}
            paint={{
              'text-color': '#ffffff',
              'text-halo-color': '#000000',
              'text-halo-width': 1,
            }}
          />
        </Source>

        {/* WITS price labels */}
        {showPrices && (
          <Source id="price-labels" type="geojson" data={substationsWithMetrics}>
            <Layer
              id="substation-price-labels"
              type="symbol"
              filter={['==', ['get', 'hasPrice'], true]}
              layout={{
                'text-field': ['get', 'price_label'],
                'text-size': 11,
                'text-offset': [0, -1.1],
                'text-anchor': 'bottom',
              }}
              paint={{
                'text-color': '#60a5fa',
                'text-halo-color': '#111827',
                'text-halo-width': 1.5,
              }}
            />
          </Source>
        )}

        {/* ============================================================= */}
        {/* TRANSPOWER ARCGIS LAYERS (LIVE DATA) */}
        {/* ============================================================= */}

        {/* Transmission Corridors (12m buffer zones) - render first as background */}
        {showTranspowerCorridors && transpowerCorridors && (
          <Source id="tp-corridors" type="geojson" data={transpowerCorridors as any}>
            <Layer
              id="tp-corridors-fill"
              type="fill"
              paint={{
                'fill-color': '#f59e0b',
                'fill-opacity': 0.15,
              }}
            />
            <Layer
              id="tp-corridors-outline"
              type="line"
              paint={{
                'line-color': '#f59e0b',
                'line-width': 1,
                'line-opacity': 0.4,
              }}
            />
          </Source>
        )}

        {/* Cook Strait Protection Zone */}
        {showCookStraitZone && cookStraitZone && (
          <Source id="tp-cook-strait" type="geojson" data={cookStraitZone as any}>
            <Layer
              id="tp-cook-strait-line"
              type="line"
              paint={{
                'line-color': '#ef4444',
                'line-width': 3,
                'line-opacity': 0.8,
                'line-dasharray': [4, 2],
              }}
            />
          </Source>
        )}

        {/* Transpower Transmission Lines (actual line routes) */}
        {showTranspowerLines && transpowerLines && (
          <Source id="tp-lines" type="geojson" data={transpowerLines as any}>
            <Layer
              id="tp-lines-layer"
              type="line"
              paint={{
                'line-color': [
                  'interpolate',
                  ['linear'],
                  ['coalesce', ['get', 'voltage_kv'], 220],
                  66, '#93c5fd',
                  110, '#60a5fa',
                  220, '#2563eb',
                  350, '#7c3aed',
                ],
                'line-width': [
                  'interpolate',
                  ['linear'],
                  ['coalesce', ['get', 'voltage_kv'], 220],
                  66, 1.5,
                  110, 2,
                  220, 2.5,
                  350, 3,
                ],
                'line-opacity': 0.7,
              }}
            />
          </Source>
        )}

        {/* Transpower Spans (line segments between structures) */}
        {showTranspowerSpans && transpowerSpans && (
          <Source id="tp-spans" type="geojson" data={transpowerSpans as any}>
            <Layer
              id="tp-spans-layer"
              type="line"
              paint={{
                'line-color': '#a855f7',
                'line-width': 1.5,
                'line-opacity': 0.6,
              }}
            />
          </Source>
        )}

        {/* Transpower Structures (towers/poles) */}
        {showTranspowerStructures && transpowerStructures && (
          <Source id="tp-structures" type="geojson" data={transpowerStructures as any}>
            <Layer
              id="tp-structures-layer"
              type="circle"
              paint={{
                'circle-radius': 3,
                'circle-color': '#f97316',
                'circle-stroke-color': '#fff',
                'circle-stroke-width': 0.5,
                'circle-opacity': 0.7,
              }}
            />
          </Source>
        )}

        {/* Transpower Sites (substations - official locations) */}
        {showTranspowerSites && transpowerSites && (
          <Source id="tp-sites" type="geojson" data={transpowerSites as any}>
            <Layer
              id="tp-sites-layer"
              type="circle"
              paint={{
                'circle-radius': 8,
                'circle-color': '#10b981',
                'circle-stroke-color': '#fff',
                'circle-stroke-width': 2,
                'circle-opacity': 0.9,
              }}
            />
            <Layer
              id="tp-sites-labels"
              type="symbol"
              layout={{
                'text-field': ['get', 'name'],
                'text-size': 10,
                'text-offset': [0, 1.2],
                'text-anchor': 'top',
              }}
              paint={{
                'text-color': '#10b981',
                'text-halo-color': '#000000',
                'text-halo-width': 1,
              }}
            />
          </Source>
        )}

        {/* Popup */}
        {popupInfo && (
          <Popup
            longitude={popupInfo.longitude}
            latitude={popupInfo.latitude}
            anchor="bottom"
            onClose={() => setPopupInfo(null)}
            closeButton={true}
            closeOnClick={false}
          >
            <div className="p-2 text-gray-900 min-w-48">
              <h3 className="font-bold text-lg">{popupInfo.feature.name}</h3>
              <p className="text-sm text-gray-600">Code: {popupInfo.feature.code}</p>
              <p className="text-sm text-gray-600">Rating: {popupInfo.feature.voltage} kV</p>
              <p className="text-sm text-gray-600">Island: {popupInfo.feature.island === 'NI' ? 'North' : 'South'}</p>

              {/* Voltage data from solver */}
              {popupInfo.feature.hasVoltageData && !showStressOverlay && (
                <div className="mt-2 pt-2 border-t border-gray-300">
                  <p className="text-xs font-semibold text-gray-700 mb-1">Power Flow Results:</p>
                  <div className="grid grid-cols-2 gap-1 text-xs">
                    <span className="text-gray-600">V magnitude:</span>
                    <span className={`font-medium ${
                      popupInfo.feature.voltage_status === 'normal' ? 'text-green-600' :
                      popupInfo.feature.voltage_status === 'warning' ? 'text-amber-600' : 'text-red-600'
                    }`}>
                      {popupInfo.feature.vm_pu?.toFixed(4)} pu
                    </span>
                    <span className="text-gray-600">V angle:</span>
                    <span className="font-medium">{popupInfo.feature.va_deg?.toFixed(2)}°</span>
                    <span className="text-gray-600">P (MW):</span>
                    <span className="font-medium">{popupInfo.feature.load_mw?.toFixed(1)}</span>
                    <span className="text-gray-600">Q (MVAr):</span>
                    <span className="font-medium">{popupInfo.feature.q_mvar?.toFixed(1)}</span>
                  </div>
                  <div className={`mt-1 text-xs font-medium px-2 py-0.5 rounded inline-block ${
                    popupInfo.feature.voltage_status === 'normal' ? 'bg-green-100 text-green-700' :
                    popupInfo.feature.voltage_status === 'warning' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {popupInfo.feature.voltage_status?.toUpperCase()}
                  </div>
                </div>
              )}

              {popupInfo.feature.hasStress && showStressOverlay && (
                <div className="mt-2 pt-2 border-t border-gray-300">
                  <p className="text-xs font-semibold text-gray-700 mb-1">Regional Stress:</p>
                  <div className="grid grid-cols-2 gap-1 text-xs">
                    <span className="text-gray-600">Load:</span>
                    <span className="font-medium">{popupInfo.feature.stress_load_mw?.toFixed(1)} MW</span>
                    <span className="text-gray-600">Capacity:</span>
                    <span className="font-medium">{popupInfo.feature.stress_capacity_mw?.toFixed(1)} MW</span>
                    <span className="text-gray-600">Stress:</span>
                    <span className="font-medium">{(popupInfo.feature.stress_level * 100).toFixed(1)}%</span>
                  </div>
                </div>
              )}

              {popupInfo.feature.hasPrice && (
                <div className="mt-2 pt-2 border-t border-gray-300">
                  <p className="text-xs font-semibold text-gray-700 mb-1">WITS Price:</p>
                  <div className="grid grid-cols-2 gap-1 text-xs">
                    <span className="text-gray-600">Price:</span>
                    <span className="font-medium">
                      ${Number(popupInfo.feature.price_nzd_mwh).toFixed(2)}/MWh
                    </span>
                    <span className="text-gray-600">Timestamp:</span>
                    <span className="font-medium">
                      {popupInfo.feature.price_timestamp
                        ? new Date(popupInfo.feature.price_timestamp).toLocaleString()
                        : 'N/A'}
                    </span>
                  </div>
                </div>
              )}

              {popupInfo.feature.gen_mw > 0 && (
                <p className="text-sm text-green-600 font-medium mt-1">
                  Generation: {popupInfo.feature.gen_mw} MW
                </p>
              )}
              {popupInfo.feature.load_mw > 0 && !popupInfo.feature.hasVoltageData && (
                <p className="text-sm text-blue-600 font-medium">
                  Load: {popupInfo.feature.load_mw} MW
                </p>
              )}
            </div>
          </Popup>
        )}
      </MapGL>

      {isUsingFallbackTopology && (
        <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
          <div className="select-none text-5xl font-black tracking-widest text-amber-400/10 rotate-[-18deg]">
            BACKUP TOPOLOGY
          </div>
        </div>
      )}

      {/* Layer controls */}
      <div className="absolute top-4 left-4 bg-gray-800/90 backdrop-blur-sm rounded-lg p-3">
        <h4 className="font-medium text-white mb-2 text-sm">Layers</h4>
        <div className="space-y-2">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showGenerators}
              onChange={(e) => setShowGenerators(e.target.checked)}
              className="rounded border-gray-600 bg-gray-700 text-green-500 focus:ring-green-500"
            />
            <span className="text-sm text-gray-300">Generators</span>
          </label>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showLoads}
              onChange={(e) => setShowLoads(e.target.checked)}
              className="rounded border-gray-600 bg-gray-700 text-blue-500 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-300">Load Centers</span>
          </label>
          {solverResult && (
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showVoltageOverlay}
                onChange={(e) => setShowVoltageOverlay(e.target.checked)}
                disabled={showStressOverlay}
                className="rounded border-gray-600 bg-gray-700 text-amber-500 focus:ring-amber-500"
              />
              <span className="text-sm text-gray-300">Voltage Colors</span>
            </label>
          )}
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showStressOverlay}
              onChange={(e) => setShowStressOverlay(e.target.checked)}
              disabled={!hasStressData}
              className="rounded border-gray-600 bg-gray-700 text-red-500 focus:ring-red-500"
            />
            <span className="text-sm text-gray-300">Stress Overlay</span>
          </label>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showPrices}
              onChange={(e) => setShowPrices(e.target.checked)}
              className="rounded border-gray-600 bg-gray-700 text-blue-500 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-300">WITS Prices</span>
          </label>
          {solverResult && (
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showLineLoading}
                onChange={(e) => setShowLineLoading(e.target.checked)}
                className="rounded border-gray-600 bg-gray-700 text-emerald-500 focus:ring-emerald-500"
              />
              <span className="text-sm text-gray-300">Line Loading</span>
            </label>
          )}

          {/* Transpower ArcGIS Layers (LIVE) */}
          <div className="pt-2 mt-2 border-t border-gray-700/60">
            <div className="flex items-center gap-1 mb-2">
              <span className="text-xs font-medium text-gray-400">Transpower Data</span>
              <DataBadge tier="LIVE" label="LIVE" showDot />
            </div>

            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showTranspowerSites}
                onChange={(e) => setShowTranspowerSites(e.target.checked)}
                className="rounded border-gray-600 bg-gray-700 text-emerald-500 focus:ring-emerald-500"
              />
              <span className="text-sm text-gray-300">Sites (Substations)</span>
              {transpowerLoading.sites && <span className="text-xs text-gray-500">...</span>}
            </label>

            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showTranspowerStructures}
                onChange={(e) => setShowTranspowerStructures(e.target.checked)}
                className="rounded border-gray-600 bg-gray-700 text-orange-500 focus:ring-orange-500"
              />
              <span className="text-sm text-gray-300">Structures (Towers)</span>
              {transpowerLoading.structures && <span className="text-xs text-gray-500">...</span>}
            </label>

            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showTranspowerSpans}
                onChange={(e) => setShowTranspowerSpans(e.target.checked)}
                className="rounded border-gray-600 bg-gray-700 text-purple-500 focus:ring-purple-500"
              />
              <span className="text-sm text-gray-300">Spans (Line Segments)</span>
              {transpowerLoading.spans && <span className="text-xs text-gray-500">...</span>}
            </label>

            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showTranspowerLines}
                onChange={(e) => setShowTranspowerLines(e.target.checked)}
                className="rounded border-gray-600 bg-gray-700 text-blue-500 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-300">Transmission Lines</span>
              {transpowerLoading.lines && <span className="text-xs text-gray-500">...</span>}
            </label>

            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showTranspowerCorridors}
                onChange={(e) => setShowTranspowerCorridors(e.target.checked)}
                className="rounded border-gray-600 bg-gray-700 text-amber-500 focus:ring-amber-500"
              />
              <span className="text-sm text-gray-300">Corridors (12m Buffer)</span>
              {transpowerLoading.corridors && <span className="text-xs text-gray-500">...</span>}
            </label>

            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showCookStraitZone}
                onChange={(e) => setShowCookStraitZone(e.target.checked)}
                className="rounded border-gray-600 bg-gray-700 text-red-500 focus:ring-red-500"
              />
              <span className="text-sm text-gray-300">Cook Strait Cable Zone</span>
              {transpowerLoading.cookStrait && <span className="text-xs text-gray-500">...</span>}
            </label>

            {/* Show counts when data is loaded */}
            {(transpowerSites || transpowerStructures || transpowerSpans || transpowerLines) && (
              <div className="text-xs text-gray-500 pt-1 space-y-0.5">
                {transpowerSites && <div>{transpowerSites.metadata.count} sites</div>}
                {transpowerStructures && <div>{transpowerStructures.metadata.count} structures</div>}
                {transpowerSpans && <div>{transpowerSpans.metadata.count} spans</div>}
                {transpowerLines && <div>{transpowerLines.metadata.count} lines</div>}
              </div>
            )}

            {/* Show errors */}
            {Object.entries(transpowerErrors).filter(([_, v]) => v).map(([key, error]) => (
              <div key={key} className="text-xs text-red-300 pt-1">
                {key}: {error}
              </div>
            ))}
          </div>

          {showPrices && witsError && (
            <div className="text-xs text-red-300 pt-1">
              WITS prices unavailable: {witsError}
            </div>
          )}
          {showPrices && !witsError && witsLoading && (
            <div className="text-xs text-gray-400 pt-1">
              Loading WITS prices...
            </div>
          )}
        </div>
      </div>

      {/* Stats panel */}
      <div className="absolute top-4 right-16 bg-gray-800/90 backdrop-blur-sm rounded-lg p-3">
        <h4 className="font-medium text-white mb-2 text-sm">NZ Grid Summary</h4>
        <div className="space-y-1 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-400">{stats.generationLabel}:</span>
            <span className="text-green-400 font-medium">{stats.totalGen.toLocaleString()} MW</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Total Load:</span>
            <span className="text-blue-400 font-medium">{stats.totalLoad.toLocaleString()} MW</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Substations:</span>
            <span className="text-white font-medium">{stats.substations}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Lines:</span>
            <span className="text-white font-medium">{stats.lines}</span>
          </div>
          {/* HVDC Inter-Island Link Status */}
          {solverResult?.hvdc_links && solverResult.hvdc_links.length > 0 && (
            <div className="pt-2 mt-2 border-t border-gray-700/60">
              <p className="text-xs font-medium text-amber-400 mb-1">HVDC Cook Strait</p>
              {solverResult.hvdc_links.map((hvdc) => (
                <div key={hvdc.id} className="space-y-0.5">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">Capacity:</span>
                    <span className="text-gray-300">{hvdc.capacity_mw} MW</span>
                  </div>
                  {hvdc.flow_mw !== null ? (
                    <>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-500">Flow:</span>
                        <span className={hvdc.flow_mw >= 0 ? 'text-amber-300' : 'text-cyan-300'}>
                          {Math.abs(hvdc.flow_mw).toFixed(1)} MW {hvdc.flow_mw >= 0 ? 'N→S' : 'S→N'}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-500">Utilization:</span>
                        <span className={`font-medium ${
                          (hvdc.utilization_percent ?? 0) < 80 ? 'text-green-400' :
                          (hvdc.utilization_percent ?? 0) < 95 ? 'text-yellow-400' : 'text-red-400'
                        }`}>
                          {hvdc.utilization_percent?.toFixed(1) ?? '0'}%
                        </span>
                      </div>
                    </>
                  ) : (
                    <div className="text-xs text-gray-500 italic">
                      Flow: Not computed (DC-OPF required)
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {/* Show HVDC info even without solver result */}
          {(!solverResult?.hvdc_links || solverResult.hvdc_links.length === 0) && (
            <div className="pt-2 mt-2 border-t border-gray-700/60">
              <p className="text-xs font-medium text-amber-400 mb-1">HVDC Cook Strait</p>
              <div className="flex justify-between text-xs">
                <span className="text-gray-500">Capacity:</span>
                <span className="text-gray-300">1200 MW</span>
              </div>
              <div className="text-xs text-gray-500 italic">
                Flow: Run solver to see flow
              </div>
            </div>
          )}
          {/* LMP Summary */}
          {solverResult?.lmp_summary && (
            <div className="pt-2 mt-2 border-t border-gray-700/60">
              <div className="flex justify-between items-center mb-1">
                <p className="text-xs font-medium text-purple-400">LMP (Model Prices)</p>
                <DataBadge
                  tier={solverResult.lmp_summary.available ? 'MODELLED' : 'SYSTEM'}
                  label={solverResult.lmp_summary.method.toUpperCase()}
                  showDot={false}
                />
              </div>
              {solverResult.lmp_summary.available ? (
                <div className="space-y-0.5">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">Min:</span>
                    <span className="text-green-400">${solverResult.lmp_summary.min_lmp?.toFixed(2)}/MWh</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">Avg:</span>
                    <span className="text-gray-300">${solverResult.lmp_summary.avg_lmp?.toFixed(2)}/MWh</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">Max:</span>
                    <span className="text-red-400">${solverResult.lmp_summary.max_lmp?.toFixed(2)}/MWh</span>
                  </div>
                  {solverResult.lmp_summary.method === 'approximate' && (
                    <div className="text-[10px] text-amber-500/70 mt-1">
                      Sensitivity-based estimate (not exact duals)
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-xs text-gray-500">
                  <span className="italic">Not computed by Python solver</span>
                  {solverResult.lmp_summary.reason && (
                    <span className="block text-gray-600 text-[10px] mt-0.5">
                      {solverResult.lmp_summary.reason}
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
          <div className="flex justify-between pt-1 border-t border-gray-700/60 text-xs">
            <span className="text-gray-500">Load Factor:</span>
            <span className="text-gray-300 font-medium">{solverConfig.load_factor.toFixed(2)}x</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-gray-500">Topology:</span>
            <DataBadge
              tier={isUsingFallbackTopology ? 'BACKUP' : 'MODELLED'}
              label={isUsingFallbackTopology ? 'BACKUP' : 'MODELLED'}
              showDot={false}
            />
          </div>
          <div className="flex justify-between items-center text-xs">
            <span className="text-gray-500">Data:</span>
            <div className="flex items-center gap-1 flex-wrap justify-end">
              <DataBadge tier={isUsingFallbackTopology ? 'BACKUP' : 'MODELLED'} label="TOPOLOGY" showDot={false} />
              <DataBadge tier="MODELLED" label="LOADS" showDot={false} />
              {showPrices && <DataBadge tier={witsError ? 'BACKUP' : 'LIVE'} label="PRICES" showDot={false} />}
              {(showTranspowerSites || showTranspowerStructures || showTranspowerSpans || showTranspowerLines || showTranspowerCorridors || showCookStraitZone) && (
                <DataBadge tier="LIVE" label="TRANSPOWER" showDot />
              )}
              {solverResult?.lmp_summary && (
                <DataBadge
                  tier={solverResult.lmp_summary.available ? 'MODELLED' : 'SYSTEM'}
                  label="LMP"
                  showDot={false}
                />
              )}
            </div>
          </div>
          {metricsProvenance?.as_of && (
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">Loads as-of:</span>
              <span className="text-gray-300 font-medium">
                {new Date(metricsProvenance.as_of).toLocaleTimeString()}
              </span>
            </div>
          )}
          {showPrices && witsProvenance?.as_of && (
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">Prices as-of:</span>
              <span className="text-gray-300 font-medium">
                {new Date(witsProvenance.as_of).toLocaleTimeString()}
              </span>
            </div>
          )}
        </div>
        <div className="mt-2 pt-2 border-t border-gray-700/60 space-y-1">
          <DataProvenanceRow
            tier={isUsingFallbackTopology ? 'BACKUP' : 'MODELLED'}
            source={isUsingFallbackTopology ? 'FALLBACK_TOPOLOGY' : '/api/v1/nz-grid/geojson'}
            asOf={metricsProvenance?.as_of}
            refresh="auto"
            inputs={{ load_factor: solverConfig.load_factor }}
            warnings={isUsingFallbackTopology ? ['Using fallback topology (API unavailable).'] : null}
            compact
          />
          {solverResult?.provenance && (
            <DataProvenanceRow
              tier={solverResult.provenance.tier as any}
              source={solverResult.provenance.source}
              asOf={solverResult.provenance.as_of}
              generatedAt={solverResult.provenance.generated_at}
              inputs={solverResult.provenance.inputs}
              warnings={solverResult.provenance.warnings}
              compact
            />
          )}
        </div>
      </div>

      {/* Current State Panel - LIVE vs MODELLED Digital Twin View */}
      <div className="absolute top-[340px] right-16 w-72">
        <CurrentStatePanel
          modelledPrices={solverResult?.bus_results?.map(b => ({
            node: b.code || b.name,
            lmp: b.lmp ?? 0,
          }))}
          modelledGeneration={stats.totalGen}
          modelledLoad={stats.totalLoad}
          isCollapsed={currentStatePanelCollapsed}
          onToggleCollapse={() => setCurrentStatePanelCollapsed(!currentStatePanelCollapsed)}
        />
      </div>

      {/* Legend */}
      <div className="absolute bottom-8 left-4 bg-gray-800/90 backdrop-blur-sm rounded-lg p-3 text-sm">
        <h4 className="font-medium text-white mb-2">Legend</h4>
        <div className="space-y-1">
          {showStressOverlay ? (
            <>
              <p className="text-xs text-gray-400 mb-1">Regional Stress</p>
              <div className="flex items-center space-x-2">
                <span className="w-3 h-3 rounded-full bg-green-500" />
                <span className="text-gray-300">Low (&lt;30%)</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="w-3 h-3 rounded-full bg-lime-500" />
                <span className="text-gray-300">Moderate</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="w-3 h-3 rounded-full bg-yellow-500" />
                <span className="text-gray-300">Elevated</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="w-3 h-3 rounded-full bg-orange-500" />
                <span className="text-gray-300">High</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="w-3 h-3 rounded-full bg-red-500" />
                <span className="text-gray-300">Critical</span>
              </div>
            </>
          ) : solverResult && showLineLoading ? (
            <>
              <p className="text-xs text-gray-400 mb-1">Line Loading</p>
              <div className="flex items-center space-x-2">
                <span className="w-4 h-1.5 bg-green-500 rounded" />
                <span className="text-gray-300">&lt;80%</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="w-4 h-1.5 bg-yellow-500 rounded" />
                <span className="text-gray-300">80–100%</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="w-4 h-1.5 bg-red-500 rounded" />
                <span className="text-gray-300">&gt;100%</span>
              </div>
            </>
          ) : showVoltageLegend ? (
            <>
              <p className="text-xs text-gray-400 mb-1">Voltage Status</p>
              <div className="flex items-center space-x-2">
                <span className="w-3 h-3 rounded-full bg-green-500" />
                <span className="text-gray-300">Normal (0.95-1.05 pu)</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="w-3 h-3 rounded-full bg-amber-500" />
                <span className="text-gray-300">Warning (0.90-1.10 pu)</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="w-3 h-3 rounded-full bg-red-500" />
                <span className="text-gray-300">Critical (&lt;0.90 or &gt;1.10 pu)</span>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center space-x-2">
                <span className="w-3 h-3 rounded-full bg-green-500" />
                <span className="text-gray-300">Generator</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="w-3 h-3 rounded-full bg-blue-400" />
                <span className="text-gray-300">Load Center</span>
              </div>
            </>
          )}
          <div className="flex items-center space-x-2 mt-2 pt-2 border-t border-gray-700">
            <span className="w-4 h-0.5 bg-green-400" />
            <span className="text-gray-300">220kV Line</span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="w-4 h-0.5 bg-amber-500" style={{ borderBottom: '2px dashed' }} />
            <span className="text-gray-300">HVDC Link</span>
          </div>

          {/* Transpower ArcGIS Legend */}
          {(showTranspowerSites || showTranspowerStructures || showTranspowerSpans || showTranspowerLines || showTranspowerCorridors || showCookStraitZone) && (
            <div className="mt-2 pt-2 border-t border-gray-700">
              <div className="flex items-center gap-1 mb-1">
                <span className="text-xs text-gray-400">Transpower (LIVE)</span>
                <DataBadge tier="LIVE" label="LIVE" showDot />
              </div>
              {showTranspowerSites && (
                <div className="flex items-center space-x-2">
                  <span className="w-3 h-3 rounded-full bg-emerald-500" />
                  <span className="text-gray-300">Sites</span>
                </div>
              )}
              {showTranspowerStructures && (
                <div className="flex items-center space-x-2">
                  <span className="w-2 h-2 rounded-full bg-orange-500" />
                  <span className="text-gray-300">Structures</span>
                </div>
              )}
              {showTranspowerSpans && (
                <div className="flex items-center space-x-2">
                  <span className="w-4 h-0.5 bg-purple-500" />
                  <span className="text-gray-300">Spans</span>
                </div>
              )}
              {showTranspowerLines && (
                <div className="flex items-center space-x-2">
                  <span className="w-4 h-0.5 bg-blue-600" />
                  <span className="text-gray-300">TX Lines</span>
                </div>
              )}
              {showTranspowerCorridors && (
                <div className="flex items-center space-x-2">
                  <span className="w-4 h-2 bg-amber-500/30 border border-amber-500" />
                  <span className="text-gray-300">Corridors</span>
                </div>
              )}
              {showCookStraitZone && (
                <div className="flex items-center space-x-2">
                  <span className="w-4 h-0.5 bg-red-500" style={{ borderBottom: '2px dashed' }} />
                  <span className="text-gray-300">Cook Strait Zone</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Solver status indicator */}
      {solverResult && (
        <div className={`absolute bottom-8 right-4 rounded-lg p-2 text-xs ${
          solverResult.converged ? 'bg-green-900/80' : 'bg-red-900/80'
        }`}>
          <span className={solverResult.converged ? 'text-green-300' : 'text-red-300'}>
            {solverResult.converged ? 'Converged' : 'Did Not Converge'}
          </span>
          <span className="text-gray-400 ml-2">{solverResult.iterations} iter</span>
        </div>
      )}
    </div>
  );
};

export default NZGridMap;
